/**
 * API Keys TanStack Query Hooks
 * ISO 27001 A.9.4.2 - Procédures de connexion sécurisée
 * Pattern: Component → Hook → API Client
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiKeysApi, type CreateApiKeyInput } from '../api/api-keys.api';
import { notifications } from '@/lib/notifications';
import i18n from '@/i18n';

// ============================================================
// Query Keys
// ============================================================
export const apiKeyKeys = {
  all: ['api-keys'] as const,
  config: () => [...apiKeyKeys.all, 'config'] as const,
  lists: () => [...apiKeyKeys.all, 'list'] as const,
  list: (page: number, limit: number) => [...apiKeyKeys.lists(), page, limit] as const,
  details: () => [...apiKeyKeys.all, 'detail'] as const,
  detail: (id: string) => [...apiKeyKeys.details(), id] as const,
  stats: (id: string) => [...apiKeyKeys.all, 'stats', id] as const,
  usage: (id: string, period: string) => [...apiKeyKeys.all, 'usage', id, period] as const,
};

// ============================================================
// Query Hooks
// ============================================================

/**
 * Hook to fetch API access configuration
 */
export function useApiAccessConfig() {
  return useQuery({
    queryKey: apiKeyKeys.config(),
    queryFn: () => apiKeysApi.getConfig(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * R-001: Hook to list API keys with pagination
 * F-010: staleTime 2min
 */
export function useApiKeys(page: number = 1, limit: number = 20) {
  return useQuery({
    queryKey: apiKeyKeys.list(page, limit),
    queryFn: () => apiKeysApi.getAll(page, limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to get a single API key by ID
 */
export function useApiKey(id: string | undefined) {
  return useQuery({
    queryKey: apiKeyKeys.detail(id!),
    queryFn: () => apiKeysApi.getById(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to get API key stats
 */
export function useApiKeyStats(id: string | undefined) {
  return useQuery({
    queryKey: apiKeyKeys.stats(id!),
    queryFn: () => apiKeysApi.getStats(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to get API key usage metrics
 */
export function useApiKeyUsage(id: string | undefined, period: string = '7d') {
  return useQuery({
    queryKey: apiKeyKeys.usage(id!, period),
    queryFn: () => apiKeysApi.getUsage(id!, period),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

// ============================================================
// Mutation Hooks
// F-009: onError par défaut sur toutes les mutations
// ============================================================

/**
 * Hook to create a new API key
 */
export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateApiKeyInput) => apiKeysApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeyKeys.lists() });
    },
    onError: (error: Error) => {
      notifications.error(error.message || i18n.t('admin.apiKeys.createApiKeyError'));
    },
  });
}

/**
 * Hook to revoke an API key
 */
export function useRevokeApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeyKeys.all });
    },
    onError: (error: Error) => {
      notifications.error(error.message || i18n.t('admin.apiKeys.revokeApiKeyError'));
    },
  });
}

/**
 * Hook to delete an API key
 */
export function useDeleteApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiKeysApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeyKeys.all });
    },
    onError: (error: Error) => {
      notifications.error(error.message || i18n.t('admin.apiKeys.deleteApiKeyError'));
    },
  });
}
